import React, { useCallback, useEffect, useState } from 'react';
import { useKeepAliveContext } from 'keepalive-for-react';
import { useUpdatePopupOpen } from '../../components/modal/updatePopupContext';
import { useMorningBriefingOpen } from '../OpsBriefing/morningBriefingOpenStore';
import { SessionReportSummaryBase } from '../../features/sessionReport/types';
import { screenCheckNeededCount } from '../../features/sessionReport/labels';
import { summaryFlowDifference, ReportIcon } from './SessionReportSessionView';
import './SessionReportGuide.css';
import GuideDialog, { GuideSlide } from '../../components/guide/GuideDialog';

/**
 * 세션 현황 화면 안내(2026-10 쉬운 화면 개편).
 *
 * - 처음 들어오면 한 번 뜬다. 닫으면 이 창(탭)에서는 다시 안 뜨고, 「다시 보지 않기」면 이 컴퓨터에서 안 뜬다.
 * - 업데이트 소식·아침 브리핑 팝업이 떠 있으면 기다렸다가 그게 닫힌 뒤에 뜬다(겹치면 무엇을 닫았는지 모른다).
 * - 화면 위 「화면 안내」 버튼으로 언제든 다시 연다.
 * 저장소 접근은 사생활 보호 모드 등에서 실패할 수 있어 전부 try 로 감싼다 — 실패하면 「안 본 것」으로 본다.
 */
const NEVER_KEY = 'rappo:sessionReport:guide:202610:never';
const SEEN_SESSION_KEY = 'rappo:sessionReport:guide:202610:seen';

const read = (storage: () => Storage, key: string) => {
    try {
        return storage().getItem(key) === '1';
    } catch {
        return false;
    }
};
const write = (storage: () => Storage, key: string) => {
    try {
        storage().setItem(key, '1');
    } catch {
        // 저장이 안 되면 다음에 한 번 더 뜰 뿐이다.
    }
};

export const useSessionReportGuide = () => {
    const updatePopupOpen = useUpdatePopupOpen();
    const briefingOpen = useMorningBriefingOpen();
    const [open, setOpen] = useState(false);
    // KeepAlive 로 다른 메뉴에 가면 화면은 내려가지 않고 비활성만 된다 — 그동안은 뜨지도, Esc 를 가로채지도 않는다.
    // 검색 조건으로 캐시가 나뉜 뒤 비활성 화면에서 처음 마운트될 수도 있다.
    // useEffectOnActive는 이때 실행되지 않으므로 true 초기값을 두면 body 포털이 새어 나온다.
    const { active } = useKeepAliveContext();
    const [autoDone, setAutoDone] = useState(
        () => read(() => window.localStorage, NEVER_KEY) || read(() => window.sessionStorage, SEEN_SESSION_KEY),
    );

    useEffect(() => {
        if (autoDone || open || !active || updatePopupOpen || briefingOpen) return;
        // 다른 팝업이 막 닫힌 직후 바로 겹쳐 뜨지 않게 조금 기다린다.
        const timer = window.setTimeout(() => {
            // 다른 캐시의 안내를 닫았으면, 마운트 때 읽었던 값으로 다시 열지 않는다.
            if (read(() => window.localStorage, NEVER_KEY) || read(() => window.sessionStorage, SEEN_SESSION_KEY)) {
                setAutoDone(true);
                return;
            }
            setOpen(true);
        }, 600);
        return () => window.clearTimeout(timer);
    }, [autoDone, open, active, updatePopupOpen, briefingOpen]);

    const close = useCallback((never: boolean) => {
        write(() => window.sessionStorage, SEEN_SESSION_KEY);
        if (never) write(() => window.localStorage, NEVER_KEY);
        setAutoDone(true);
        setOpen(false);
    }, []);

    // 다른 팝업이 나중에 떠도 겹치지 않게, 떠 있는 동안은 숨겼다가 닫히면 다시 보인다(수동으로 연 것도 같다).
    // 내리지 않고 숨기기만 해서 보던 장·「다시 보지 않기」 체크가 그대로 남는다.
    return { open, hidden: !active || updatePopupOpen || briefingOpen, show: () => setOpen(true), close };
};

type Example = SessionReportSummaryBase | null;
const number = (value: number | undefined, signed = false) => value === undefined ? '—'
    : `${signed && value > 0 ? '+' : ''}${value.toLocaleString('ko-KR').replace('-', '−')}`;

const Preview = ({ label = '화면 예시', compact = false, children }: { label?: string; compact?: boolean; children: React.ReactNode }) => (
    <figure className={`session-guide-preview${compact ? ' session-guide-preview-compact' : ''}`} aria-label={label}>
        <figcaption>{label}</figcaption>
        {children}
    </figure>
);

const FlowPreview = ({ example: t, scope }: { example: Example; scope: string }) => {
    const difference = t ? summaryFlowDifference(t) : undefined;
    const cards = [
        { label: '지난달 남은', value: t?.prevRemaining, detail: '지난달 말 기준' },
        { label: '새로 판', value: t?.registeredSessions, signed: true, detail: '신규 · 재등록(리뉴) · 추가(세션업)' },
        { label: '수업', value: t ? -t.progressed : undefined, signed: true, detail: '출석 · 노쇼',
            extra: `강사 간 넘김 ${number(t?.handoffSessions, true)}` },
        { label: '취소·정리', value: t ? t.closed ?? 0 : undefined, signed: true, detail: '취소 · 삭제 · 인계',
            extra: `환불 ${number(t?.refundSessions, true)}` },
        ...(difference !== 0 ? [{ label: '기록과 안 맞는 회차', value: difference, signed: true, detail: '기록 사이의 차이', tone: 'difference' }] : []),
        { label: '남은 회차', value: t?.remaining, detail: '수강권에 남은 회차', tone: 'remaining' },
    ];
    return <Preview label={t ? `${scope === '센터 전체' ? '지금 보고서의 센터 전체' : `${scope} · 지금 보고서`} · 단위: 회` : '회차 흐름 모양 · 단위: 회'}>
        <div className="session-guide-flow">
            {cards.map((card, index) => <React.Fragment key={card.label}>
                {index > 0 && <span className="session-guide-sign" aria-hidden="true">{card.tone === 'remaining' ? '=' : '→'}</span>}
                <div className={`session-guide-flow-card ${card.tone ?? ''}`}>
                    <span>{card.label}</span>
                    <strong>{number(card.value, card.signed)}</strong>
                    <small>{card.detail}</small>
                    {card.tone !== 'difference' ? <span className="session-guide-link">근거 보기 ›</span>
                        : t && screenCheckNeededCount(t.reference) > 0 && <span className="session-guide-link">손볼 회원 보기 ›</span>}
                    {card.extra && <small className="session-guide-flow-extra">{card.extra}</small>}
                </div>
            </React.Fragment>)}
        </div>
        {!!t?.reference?.sessionlessProgressed && <p className="session-guide-preview-note">무제한 수강권 수업은 수업 수에 포함하고, 회차 계산에서는 빼요.</p>}
    </Preview>;
};

// 총괄/강사 시트의 실제 머리글과 시트 이름(excel.ts, labels.ts). 영어 머리글은 안내에서 생략한다.
export const sessionGuideExcelNames = (ghostDays: number): [string, string, string][] => [
    ['지난달 남은', '전월잔여', '전월잔여'],
    ['새로 판', '당월등록', '세션'],
    ['수업', '당월완료', '진행세션'],
    ['강사 간 넘김', '인계 내역 · 세션', '인계세션'],
    ['남은 회차', '남은세션', '남은세션'],
    ['취소·정리', '소멸(참고)', '소멸'],
    ['상태', '확인 필요', '확인'],
    [`${ghostDays}일 넘게 안 옴`, '—', `유령 시트 (${ghostDays}일 넘게 안 옴)`],
];

const buildSlides = ({ example, scope, ghostDays }: { example: Example; scope: string; ghostDays: number }): GuideSlide[] => [
    {
        title: '이번 달 세션 흐름 읽는 법',
        body: '왼쪽부터 읽으면 회차가 어떻게 바뀌었는지 알 수 있어요. 숫자를 누르면 어디서 나왔는지 보여요.',
        preview: <FlowPreview example={example} scope={scope} />,
        content: <div className="session-guide-note">
            <ReportIcon name="info" />
            <p>강사 간 넘김과 환불도 함께 반영해요. 기록이 서로 다르면 「기록과 안 맞는 회차」가 따로 보여요.</p>
        </div>,
    },
    {
        title: '손볼 회원부터 확인해요',
        body: '노란 띠의 「확인 필요」를 누르면 할 일별 회원 목록이 열려요. 회원을 눌러 내용을 살펴봐요.',
        preview: <Preview>
            <div className="session-guide-review-strip"><ReportIcon name="alert" /><strong>손볼 회원 3건 확인 필요</strong><span>보기 ›</span></div>
            <div className="session-guide-review-list">
                <div><span className="session-guide-tag ask">라포가 확인할 것</span><span>김회원</span><span className="session-guide-link">수강권 확인 ›</span></div>
                <div><span className="session-guide-tag fix">직접 고칠 것</span><span>이회원</span><span className="session-guide-link">수강권 확인 ›</span></div>
            </div>
        </Preview>,
        content: <div className="session-guide-explain">
            <p><strong>라포가 확인할 것</strong><span>버튼 하나로 라포에 알려 주세요. 따로 연락하지 않으셔도 돼요.</span></p>
            <p><strong>직접 고칠 것</strong><span>수강권 정보나 수업 기록을 고쳐 주세요.</span></p>
            <div className="session-guide-note">확인한 뒤 「문제없어요 · 처리 완료」로 목록에서 뺄 수 있어요. 숫자가 바뀌면 다시 보여요.</div>
        </div>,
    },
    {
        title: '회원 한 명 자세히 살펴봐요',
        body: '회원 이름을 누르면 수강권과 수업 기록이 열려요. 쓴 회차와 총회차를 먼저 확인해요.',
        preview: <Preview>
            <div className="session-guide-member">
                <div className="session-guide-member-heading"><strong>김회원</strong><span>개인 수업 수강권</span><b>8 / 20회</b></div>
                <div className="session-guide-progress"><span /></div>
                <p>쓴 회차/총회차 · 20회 중 8회 썼어요</p>
                <div className="session-guide-lesson"><span>지난 수업 · 출석</span><strong>7회차</strong><span className="session-guide-move">다른 수강권으로</span></div>
                <div className="session-guide-lesson"><span>최근 수업 · 출석</span><strong>8회차</strong><span className="session-guide-move">다른 수강권으로</span></div>
            </div>
        </Preview>,
        content: <div className="session-guide-explain">
            <p><strong>수업마다 「몇 회차」</strong><span>수강권에서 몇 번째로 쓴 수업인지 보여요.</span></p>
            <div className="session-guide-note"><ReportIcon name="arrow" /><p>잘못 차감된 수업은 「다른 수강권으로」를 눌러 옮겨요. 옮길 수강권을 고르고 확인해요.</p></div>
        </div>,
    },
    {
        title: '강사별로 보기 · 엑셀',
        body: '강사를 고르면 그 강사의 숫자와 회원이 보여요. 「엑셀 칸 모두」로 자세히 보고, 「엑셀 받기」로 저장해요.',
        preview: <Preview compact>
            <div className="session-guide-toolbar">
                <span className="session-guide-select">강사 전체 <ReportIcon name="down" /></span>
                <span className="session-guide-toggle"><span>기본 보기</span><strong>엑셀 칸 모두</strong></span>
                <span className="session-guide-download"><ReportIcon name="download" />엑셀 받기</span>
            </div>
        </Preview>,
        content: <table className="session-guide-excel">
            <caption className="sr-only">화면 이름과 엑셀 칸 이름</caption>
            <thead><tr><th scope="col">화면 이름</th><th scope="col">엑셀 총괄</th><th scope="col">엑셀 강사별</th></tr></thead>
            <tbody>{sessionGuideExcelNames(ghostDays).map(([label, summary, coach]) => <tr key={label}><th scope="row">{label}</th><td>{summary}</td><td>{coach}</td></tr>)}</tbody>
        </table>,
    },
];

const SessionReportGuide = ({ onClose, example = null, scope = '센터 전체', ghostDays = 30, hidden = false }: {
    onClose: (never: boolean) => void;
    example?: Example;
    scope?: string;
    ghostDays?: number;
    hidden?: boolean;
}) => <GuideDialog badge="세션 현황 안내" slides={buildSlides({ example, scope, ghostDays })} onClose={onClose} hidden={hidden} variant="preview" />;

export default SessionReportGuide;
